import React from 'react';
import PropTypes from 'prop-types';

import {Paper, Stack, Typography, Button, Box, SplitButtonDropdown} from 'views/components';
import {Add as AddIcon, DescriptionOutlined as DescriptionOutlinedIcon} from 'views/icons';
import {MarkdownViewer, MarkdownEditor, Translate, translate, isInactiveEvent} from 'views/module';

const NotesTile = (props) => {
  const {event, onUpdateEventInfo, onUpdateAllEventInfo} = props;

  // Notes belong to a shift that still happens, so an inactive event keeps its notes read only.
  const isInactive = isInactiveEvent(event);

  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState(event.info.description.blob);
  const [splitButtonOpen, setSplitButtonOpen] = React.useState(false);

  const splitButtonAnchorRef = React.useRef(null);

  const handleSave = async (val) => {
    event.info.description.blob = val;
    const info = {
      title: event.info.title,
      description: {
        blob: val,
        editorType: 'MARKDOWN',
      },
    };
    await onUpdateEventInfo(
        event.clientData.clientRn,
        event.eventRn,
        info,
        event.shiftType,
        () => {
          setEditing(false);
        },
    );
  };

  const handleSaveAll = async (val) => {
    const info = {
      title: event.info.title,
      description: {
        blob: val,
        editorType: 'MARKDOWN',
      },
    };
    await onUpdateAllEventInfo(
        event.clientData.clientRn,
        event.recurrenceRn,
        info,
        event.shiftType,
        () => {
          setEditing(false);
        },
    );
  };
  const handleCancel = () => {
    setValue(event.info.description.blob);
    setEditing(false);
  };
  const handleSplitButtonClickAway = () => setSplitButtonOpen(false);

  return (
    <Paper sx={{p: 2, mb: 2, borderRadius: 4}}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <DescriptionOutlinedIcon sx={{width: 24, height: 24, color: '#b0b0b0'}} />
          <Typography variant="h6">
            <Translate expression='schedule.eventDetail.NotesSection.Title' />
          </Typography>
        </Stack>
        {
          !editing && !isInactive &&
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setEditing(true)}
            sx={{
              'color': 'black',
              'backgroundColor': '#ffffff',
              'border': '1px solid #e0e0e0',
              'borderRadius': 2,
              'textTransform': 'none',
              'fontSize': '14px',
              'fontWeight': 'normal',
              '& .MuiButton-startIcon': {
                marginRight: '3px',
              },
              '&:hover': {
                backgroundColor: '#f8f9fa',
                borderColor: '#d0d0d0',
              },
            }}
          >
            <Translate expression='schedule.eventDetail.NotesSection.AddNoteButton' />
          </Button>
        }
      </Stack>
      <Box sx={{background: '#f7f7f7', p: 1.5, borderRadius: 1, position: 'relative'}}>
        {
          editing &&
          <>
            <MarkdownEditor
              value={value}
              onChange={setValue}
            />
            <Stack direction='row' justifyContent='flex-end' spacing={1} pt={1}>
              <Button onClick={handleCancel}>
                <Translate expression='generic.Cancel'/>
              </Button>
              {
                event.type === 'RECURRENCE_EVENT' &&
                <SplitButtonDropdown
                  options={[
                    {key: 'SAVE', text: 'Save'},
                    {key: 'SAVE_ALL', text: 'Save All'},
                  ]}
                  open={splitButtonOpen}
                  selectedIndex={0}
                  label={'Save'}
                  onClickPrimaryButton={() => handleSave(value)}
                  onClickArrowButton={() => setSplitButtonOpen((open) => !open)}
                  onClickAway={handleSplitButtonClickAway}
                  onMenuItemClick={(_event, index) => {
                    setSplitButtonOpen(false);
                    if (index === 0) handleSave(value);
                    if (index === 1) handleSaveAll(value);
                  }}
                  loading={false}
                  sx={{minWidth: '64px', height: '32px', fontSize: '0.875rem'}}
                  anchorRef={splitButtonAnchorRef}
                />
              }
              {
                event.type === 'FIXED' &&
                <Button
                  variant="contained"
                  onClick={() => handleSave(value)}
                  sx={{minWidth: '100px'}}
                >
                  <Translate expression='generic.Save' />
                </Button>
              }
            </Stack>
          </>
        }
        {
          !editing &&
          <>
            <MarkdownViewer
              value={value || translate('schedule.eventDetail.NotesSection.NoNotes')}
              truncate={true}
            />
          </>
        }
      </Box>
    </Paper>
  );
};

NotesTile.propTypes = {
  event: PropTypes.object.isRequired,
  onUpdateEventInfo: PropTypes.func.isRequired,
  onUpdateAllEventInfo: PropTypes.func.isRequired,
};


export default NotesTile;
